/*
 * MCC delivery info
 * "Ships from" badge, "In stock" line and estimated delivery for the visitor's country.
 * Data: product metafield mcc.delivery (filled automatically from Printify by the MCC stock sync).
 * Settings: block "MCC Delivery info" in the product section (theme editor).
 */
(() => {
  if (customElements.get('mcc-delivery')) return;

  const EUROPE = new Set(
    'AD AL AT AX BA BE BG BY CH CY CZ DE DK EE ES FI FO FR GB GG GI GR HR HU IE IM IS IT JE LI LT LU LV MC MD ME MK MT NL NO PL PT RO RS SE SI SK SM UA VA XK'.split(' ')
  );
  const OCEANIA = new Set(['AU', 'NZ']);

  // Is the visitor in the same shipping region as the origin?
  const sameRegion = (origin, country) => {
    if (!origin || !country) return false;
    if (EUROPE.has(origin)) return EUROPE.has(country);
    if (OCEANIA.has(origin)) return OCEANIA.has(country);
    return origin === country;
  };

  const isPreview = () => {
    const s = window.Shopify || {};
    return Boolean(s.designMode) || Boolean(s.theme && s.theme.role && s.theme.role !== 'main');
  };

  let countryPromise = null;

  // The store's default market country. Shopify reports it (and sets the localization cookie to it)
  // for every visitor who has not picked another country, so it says nothing about where they are.
  const DEFAULT_COUNTRY = 'US';

  // Visitor country: a country picked in the store's selector wins; otherwise Shopify's IP based detection.
  const detectCountry = () => {
    if (countryPromise) return countryPromise;
    countryPromise = (async () => {
      const s = window.Shopify || {};
      const current = s.country ? String(s.country).toUpperCase() : null;
      if (current && current !== DEFAULT_COUNTRY) return current;
      try {
        const cached = sessionStorage.getItem('mcc-country');
        if (cached) return cached;
      } catch (e) {}
      let country = null;
      try {
        const root = (s.routes && s.routes.root) || '/';
        const res = await fetch(`${root}browsing_context_suggestions.json`, { credentials: 'same-origin' });
        if (res.ok) {
          const json = await res.json();
          country = json && json.detected_values && json.detected_values.country && json.detected_values.country.handle;
        }
      } catch (e) {}
      if (country) {
        country = String(country).toUpperCase();
        try {
          sessionStorage.setItem('mcc-country', country);
        } catch (e) {}
      }
      return country || current;
    })();
    return countryPromise;
  };

  const transitDays = (data, country) => {
    let fallback = null;
    for (const group of data.t || []) {
      const [min, max, countries] = group;
      if (countries.includes(country)) return [min, max];
      if (countries.includes('*')) fallback = [min, max];
    }
    return fallback;
  };

  const addBusinessDays = (start, days) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) added += 1;
    }
    return d;
  };

  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (m, key) => (key in values ? values[key] : m));

  class MccDelivery extends HTMLElement {
    connectedCallback() {
      this.render();
      // Follow the selected size and colour, so "In stock" is only shown for a variant that can be bought.
      this.scope = this.closest('product-info') || document;
      this.onVariantChange = () => {
        clearTimeout(this.stockTimer);
        this.updateStock();
        this.stockTimer = setTimeout(() => this.updateStock(), 400);
      };
      this.scope.addEventListener('change', this.onVariantChange);
      window.addEventListener('popstate', this.onVariantChange);
    }

    disconnectedCallback() {
      if (this.scope) this.scope.removeEventListener('change', this.onVariantChange);
      window.removeEventListener('popstate', this.onVariantChange);
      clearTimeout(this.stockTimer);
    }

    readJson(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    currentVariantId() {
      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl) return fromUrl;
      const input = (this.scope || document).querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && input.value) return String(input.value);
      return this.dataset.variant || '';
    }

    variantAvailable() {
      const map = this.variants || {};
      const id = this.currentVariantId();
      if (id in map) return Boolean(map[id]);
      return this.dataset.available === 'true';
    }

    // "In stock" and the delivery date only make sense for a size and color that can be bought.
    updateStock() {
      const available = this.variantAvailable();
      const row = this.querySelector('[data-mcc-stock]');
      const etaRow = this.querySelector('[data-mcc-eta]');
      if (row) row.hidden = !(this.stockWanted && available);
      if (etaRow) etaRow.hidden = !(this.etaWanted && available);
      this.refreshVisibility();
    }

    refreshVisibility() {
      const rows = ['[data-mcc-origin]', '[data-mcc-stock]', '[data-mcc-eta]'].map((sel) => this.querySelector(sel));
      const visible = rows.some((row) => row && !row.hidden);
      const note = this.querySelector('[data-mcc-note]');
      if (note) note.hidden = !visible || !note.textContent.trim();
      this.hidden = !visible;
      if (visible) requestAnimationFrame(() => this.classList.add('is-ready'));
    }

    async render() {
      const data = this.readJson('[data-mcc-data]');
      const text = this.readJson('[data-mcc-text]') || {};
      this.variants = this.readJson('[data-mcc-variants]') || {};
      text.badge = text.badge || 'Ships from {origin}';
      text.range = text.range || '{from} to {to}';
      text.days = text.days || '{min} to {max} business days';
      text.stock = text.stock || 'In stock';
      if (!data || !data.o) return;

      const params = new URLSearchParams(window.location.search);
      let country = null;
      if (isPreview()) country = (params.get('mcc_country') || this.dataset.previewCountry || '').toUpperCase() || null;
      if (!country) country = await detectCountry();

      const origin = String(data.o).toUpperCase();
      const match = sameRegion(origin, country);
      const badgeMode = this.dataset.badgeMode;
      const etaMode = this.dataset.etaMode;
      const stockMode = this.dataset.stockMode || 'always';

      const showBadge = badgeMode === 'always' || (badgeMode === 'match' && match);
      const days = country ? transitDays(data, country) : null;
      const etaAllowed = etaMode === 'always' || (etaMode === 'match' && match);

      // Production days: the provider's real average for this product (Printify, last 30 days) or the fixed setting.
      let pmin = Number(this.dataset.pmin || 0);
      let pmax = Number(this.dataset.pmax || 0);
      const avg = Number(data.m);
      if (this.dataset.prodSource !== 'fixed' && avg > 0) {
        pmin = Math.max(1, Math.floor(avg));
        pmax = Math.max(pmin, Math.ceil(avg) + 1);
      }
      const extra = Number(this.dataset.extra || 0);
      const minDays = days ? pmin + days[0] : 0;
      const maxDays = days ? Math.max(minDays, pmax + days[1] + extra) : 0;
      const limit = Number(this.dataset.maxDays || 0);
      const tooLong = !days || (limit > 0 && maxDays > limit);

      // A date is shown only when delivery is short; otherwise no date at all.
      const showEta = etaAllowed && !tooLong;
      this.etaWanted = showEta;
      this.stockWanted = stockMode === 'always' || (stockMode === 'no_date' && !showEta);

      const originRow = this.querySelector('[data-mcc-origin]');
      const etaRow = this.querySelector('[data-mcc-eta]');
      const stockText = this.querySelector('[data-mcc-stock-text]');

      if (originRow) {
        originRow.hidden = !showBadge;
        if (showBadge) {
          const names = text.names || {};
          let originName = names[origin];
          if (!originName && EUROPE.has(origin)) originName = names.EU;
          if (!originName) {
            try {
              originName = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' }).of(origin);
            } catch (e) {
              originName = origin;
            }
          }
          this.querySelector('[data-mcc-origin-text]').textContent = fill(text.badge, { origin: originName });
        }
      }

      if (stockText) stockText.textContent = text.stock;

      if (etaRow) {
        if (showEta) {
          const locale = document.documentElement.lang || 'en';

          let countryName = country;
          try {
            countryName = new Intl.DisplayNames([locale], { type: 'region' }).of(country) || country;
          } catch (e) {}
          let label = text.label || '';
          if (text.showCountry && text.countryText) label = `${label} ${fill(text.countryText, { country: countryName })}`;

          let value;
          if (this.dataset.etaFormat === 'days') {
            value = fill(text.days, { min: minDays, max: maxDays });
          } else {
            const opts = { month: 'short', day: 'numeric' };
            if (this.dataset.etaFormat === 'dates_weekday') opts.weekday = 'short';
            const fmt = new Intl.DateTimeFormat(locale, opts);
            const today = new Date();
            const from = fmt.format(addBusinessDays(today, minDays));
            const to = fmt.format(addBusinessDays(today, maxDays));
            value = from === to ? from : fill(text.range, { from, to });
          }
          this.querySelector('[data-mcc-eta-label]').textContent = label.trim();
          this.querySelector('[data-mcc-eta-value]').textContent = value;
        }
      }

      this.updateStock();
    }
  }

  customElements.define('mcc-delivery', MccDelivery);
})();
